import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService, FuncionarioCacheBiometrico } from '../../api.service';
import { ServicoCriptografiaLocal } from './servico-criptografia-local';

export interface FuncionarioBiometriaLocal extends FuncionarioCacheBiometrico {
  empregadorId: string;
  dispositivoId: string;
  geradoEm: string;
  expiraEm: string;
  atualizadoEm: string;
}

export interface EstadoCacheBiometricoLocal {
  totalFuncionarios: number;
  valido: boolean;
  expiraEm?: string;
  atualizadoEm?: string;
}

interface FuncionarioBiometriaLocalArmazenado extends Partial<FuncionarioBiometriaLocal> {
  chaveCache: string;
  dadosCriptografados?: string;
}

@Injectable({ providedIn: 'root' })
export class ServicoCacheBiometricoLocal {
  private readonly nomeBanco = 'pontojwm-terminal';
  private readonly versaoBanco = 3;
  private readonly nomeArmazenamento = 'cache-biometrico-funcionarios';
  private readonly contextoCriptografia = 'cache-biometrico-offline';
  private banco?: Promise<IDBDatabase>;

  constructor(
    private readonly api: ApiService,
    private readonly servicoCriptografiaLocal: ServicoCriptografiaLocal
  ) {}

  async atualizarCache(terminalToken: string): Promise<EstadoCacheBiometricoLocal> {
    const resposta = await firstValueFrom(this.api.buscarCacheBiometricoTerminal(terminalToken));
    const atualizadoEm = new Date().toISOString();
    await this.substituirCache(terminalToken, resposta.funcionarios.map(funcionario => ({
      ...funcionario,
      empregadorId: resposta.empregadorId,
      dispositivoId: resposta.dispositivoId,
      geradoEm: resposta.geradoEm,
      expiraEm: resposta.expiraEm,
      atualizadoEm
    })));
    return this.estado(terminalToken);
  }

  async listarFuncionariosValidos(tokenTerminal: string): Promise<FuncionarioBiometriaLocal[]> {
    const funcionarios = await this.listarTodos(tokenTerminal);
    const agora = new Date();
    return funcionarios.filter(funcionario => new Date(funcionario.expiraEm) > agora);
  }

  async estado(tokenTerminal: string): Promise<EstadoCacheBiometricoLocal> {
    const funcionarios = await this.listarTodos(tokenTerminal);
    if (!funcionarios.length) {
      return { totalFuncionarios: 0, valido: false };
    }

    const expiraEm = funcionarios
      .map(funcionario => funcionario.expiraEm)
      .sort()[0];
    const atualizadoEm = funcionarios
      .map(funcionario => funcionario.atualizadoEm)
      .sort()
      .at(-1);

    return {
      totalFuncionarios: funcionarios.length,
      valido: new Date(expiraEm) > new Date(),
      expiraEm,
      atualizadoEm
    };
  }

  private async substituirCache(tokenTerminal: string, funcionarios: FuncionarioBiometriaLocal[]) {
    const banco = await this.abrirBanco();
    const funcionariosCriptografados = await Promise.all(funcionarios.map(async funcionario => ({
      chaveCache: this.chaveCacheFuncionario(funcionario),
      dadosCriptografados: await this.servicoCriptografiaLocal.criptografarJson(
        funcionario,
        tokenTerminal,
        this.contextoCriptografia
      )
    })));
    return new Promise<void>((resolve, reject) => {
      const transacao = banco.transaction(this.nomeArmazenamento, 'readwrite');
      const armazenamento = transacao.objectStore(this.nomeArmazenamento);
      armazenamento.clear();
      for (const funcionario of funcionariosCriptografados) {
        armazenamento.put(funcionario);
      }
      transacao.oncomplete = () => resolve();
      transacao.onerror = () => reject(transacao.error);
    });
  }

  private async listarTodos(tokenTerminal: string) {
    const banco = await this.abrirBanco();
    return new Promise<FuncionarioBiometriaLocalArmazenado[]>((resolve, reject) => {
      const transacao = banco.transaction(this.nomeArmazenamento, 'readonly');
      const requisicao = transacao.objectStore(this.nomeArmazenamento).getAll();
      requisicao.onsuccess = () => resolve(requisicao.result as FuncionarioBiometriaLocalArmazenado[]);
      requisicao.onerror = () => reject(requisicao.error);
    }).then(funcionarios => Promise.all(funcionarios.map(funcionario => this.abrirFuncionario(tokenTerminal, funcionario))));
  }

  private async abrirFuncionario(tokenTerminal: string, funcionario: FuncionarioBiometriaLocalArmazenado): Promise<FuncionarioBiometriaLocal> {
    if (!funcionario.dadosCriptografados) {
      return funcionario as FuncionarioBiometriaLocal;
    }
    return this.servicoCriptografiaLocal.descriptografarJson<FuncionarioBiometriaLocal>(
      funcionario.dadosCriptografados,
      tokenTerminal,
      this.contextoCriptografia
    );
  }

  private abrirBanco() {
    this.banco ??= new Promise<IDBDatabase>((resolve, reject) => {
      const requisicao = indexedDB.open(this.nomeBanco, this.versaoBanco);
      requisicao.onupgradeneeded = evento => {
        const banco = requisicao.result;
        if (!banco.objectStoreNames.contains('registros-ponto-pendentes')) {
          banco.createObjectStore('registros-ponto-pendentes', { keyPath: 'offlineId' });
        }
        if (!banco.objectStoreNames.contains(this.nomeArmazenamento)) {
          banco.createObjectStore(this.nomeArmazenamento, { keyPath: 'chaveCache' });
        } else if (evento.oldVersion < 3) {
          banco.deleteObjectStore(this.nomeArmazenamento);
          banco.createObjectStore(this.nomeArmazenamento, { keyPath: 'chaveCache' });
        }
      };
      requisicao.onsuccess = () => resolve(requisicao.result);
      requisicao.onerror = () => reject(requisicao.error);
    });
    return this.banco;
  }

  private chaveCacheFuncionario(funcionario: FuncionarioBiometriaLocal) {
    return funcionario.faceId || funcionario.funcionarioId;
  }
}
